
// client/src/components/common/TimeSelect.jsx
/**
 * =================================================================
 * Compact Time Select Component
 * =================================================================
 * Breaks time selection into small individual dropdowns:
 * - Hour (1-12)
 * - Minute (00, 05, 10, ..., 55) - 5-minute increments only
 * - Period (AM/PM)
 * - Optional timezone display
 * 
 * Much more compact than single dropdown with 288 options!
 * =================================================================
 */

import React, { useMemo } from 'react';
import './TimeSelect.css';
import { TIMEZONES, getTimezoneOffset } from '../utils/timezoneUtils';

/**
 * Parse HH:MM format into 12-hour components
 */
const parseTime = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) {
    return { hour: '9', minute: '00', period: 'AM' };
  }
  
  const [hourStr, minuteStr] = timeStr.split(':');
  const hour24 = parseInt(hourStr, 10);
  const minute = minuteStr || '00';
  
  // Round minutes to nearest 5
  const roundedMin = Math.round(parseInt(minute, 10) / 5) * 5;
  const finalMin = roundedMin === 60 ? 0 : roundedMin;
  const finalHour24 = roundedMin === 60 ? (hour24 + 1) % 24 : hour24;
  
  const period = finalHour24 >= 12 ? 'PM' : 'AM';
  const hour12 = finalHour24 === 0 ? 12 : finalHour24 > 12 ? finalHour24 - 12 : finalHour24;
  
  return {
    hour: String(hour12),
    minute: String(finalMin).padStart(2, '0'),
    period
  };
};

/**
 * Convert 12-hour components to HH:MM format
 */
const formatTo24Hour = (hour12, minute, period) => {
  let hour24 = parseInt(hour12, 10);
  
  if (period === 'AM') {
    if (hour24 === 12) hour24 = 0;
  } else {
    if (hour24 !== 12) hour24 += 12;
  }
  
  return `${String(hour24).padStart(2, '0')}:${minute}`;
};

/**
 * TimeSelect Component
 * @param {string} value - Current time in HH:MM format
 * @param {function} onChange - Callback with HH:MM format
 * @param {string} tenantTimezone - Tenant's timezone
 * @param {boolean} showTimezone - Show timezone label
 * @param {boolean} disabled - Disable all selects
 * @param {string} className - Additional CSS classes
 * @param {string} id - Base ID for inputs
 * @param {boolean} required - Mark as required
 */
const TimeSelect = ({
  value = '09:00',
  onChange,
  tenantTimezone = 'America/Chicago',
  showTimezone = true,
  disabled = false,
  className = '',
  id = 'time-select',
  required = false
}) => {
  // Parse current value
  const { hour, minute, period } = useMemo(() => parseTime(value), [value]);
  
  // Generate options
  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const h = i + 1;
    return { value: String(h), label: String(h) };
  });
  
  const minuteOptions = Array.from({ length: 12 }, (_, i) => {
    const m = i * 5;
    const str = String(m).padStart(2, '0');
    return { value: str, label: str };
  });
  
  const periodOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' }
  ];
  
  // Handle changes
  const handleHourChange = (e) => {
    const newTime = formatTo24Hour(e.target.value, minute, period);
    onChange?.(newTime);
  };
  
  const handleMinuteChange = (e) => {
    const newTime = formatTo24Hour(hour, e.target.value, period);
    onChange?.(newTime);
  };
  
  const handlePeriodChange = (e) => {
    const newTime = formatTo24Hour(hour, minute, e.target.value);
    onChange?.(newTime);
  };
  
  // Timezone info
  const timezoneLabel = TIMEZONES[tenantTimezone] || tenantTimezone;
  const timezoneOffset = getTimezoneOffset(tenantTimezone);
  
  return (
    <div className={`time-select-compact-wrapper ${className}`}>
      <div className="time-select-compact-inputs">
        {/* Hour */}
        <select
          id={`${id}-hour`}
          value={hour}
          onChange={handleHourChange}
          disabled={disabled}
          required={required}
          className="time-select-compact-part time-select-compact-hour"
          aria-label="Hour"
        >
          {hourOptions.map(({ value: val, label }) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>
        
        <span className="time-select-compact-separator">:</span>
        
        {/* Minute */}
        <select
          id={`${id}-minute`}
          value={minute}
          onChange={handleMinuteChange}
          disabled={disabled}
          required={required}
          className="time-select-compact-part time-select-compact-minute"
          aria-label="Minute"
        >
          {minuteOptions.map(({ value: val, label }) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>
        
        {/* AM/PM */}
        <select
          id={`${id}-period`}
          value={period}
          onChange={handlePeriodChange}
          disabled={disabled}
          required={required}
          className="time-select-compact-part time-select-compact-period"
          aria-label="AM/PM"
        >
          {periodOptions.map(({ value: val, label }) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>
      </div>
      
      {/* Timezone info */}
      {showTimezone && (
        <small className="time-select-compact-tz-info">
          🌍 {timezoneLabel} ({timezoneOffset})
        </small>
      )}
    </div>
  );
};

export default TimeSelect;